import { useState, useEffect } from 'react';

/**
 * Custom hook for typewriter animated placeholder text.
 * Cycles through an array of placeholder suggestions with typing and deleting animations.
 *
 * @param {string[]} suggestions - Array of placeholder strings to cycle through
 * @param {Object} [options] - Configuration options
 * @param {number} [options.typingSpeed=50] - Base typing speed in ms (randomised +0-50ms)
 * @param {number} [options.deletingSpeed=15] - Base deleting speed in ms (randomised +0-15ms)
 * @param {number} [options.pauseAtFull=2000] - Pause duration at full text in ms
 * @param {number} [options.pauseBetween=500] - Pause duration between suggestions in ms
 * @returns {{ currentPlaceholder: string }}
 */
const useAnimatedPlaceholder = (suggestions, options = {}) => {
  const {
    typingSpeed = 50,
    deletingSpeed = 15,
    pauseAtFull = 2000,
    pauseBetween = 500,
  } = options;

  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(true);
  const [randomOrder, setRandomOrder] = useState([]);
  const [showCursor, setShowCursor] = useState(true);

  // Initialise random order on mount
  useEffect(() => {
    const shuffled = [...Array(suggestions.length).keys()]
      .sort(() => Math.random() - 0.5);
    setRandomOrder(shuffled);
  }, [suggestions.length]);

  // Blinking cursor effect
  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 500);

    return () => clearInterval(cursorInterval);
  }, []);

  useEffect(() => {
    if (randomOrder.length === 0) return;

    const currentSuggestion = suggestions[randomOrder[currentIndex]];
    let timeoutId;

    if (isTyping) {
      // Typing animation
      if (displayText.length < currentSuggestion.length) {
        timeoutId = setTimeout(() => {
          setDisplayText(currentSuggestion.slice(0, displayText.length + 1));
        }, typingSpeed + Math.random() * typingSpeed);
      } else {
        // Finished typing, wait before deleting
        timeoutId = setTimeout(() => {
          setIsTyping(false);
        }, pauseAtFull);
      }
    } else {
      // Deleting animation
      if (displayText.length > 0) {
        timeoutId = setTimeout(() => {
          setDisplayText(displayText.slice(0, -1));
        }, deletingSpeed + Math.random() * deletingSpeed);
      } else {
        // Finished deleting, move to next suggestion
        timeoutId = setTimeout(() => {
          setCurrentIndex(prevIndex => (prevIndex + 1) % randomOrder.length);
          setIsTyping(true);

          // Reshuffle when we complete a full cycle
          if ((currentIndex + 1) % randomOrder.length === 0) {
            const newShuffled = [...Array(suggestions.length).keys()]
              .sort(() => Math.random() - 0.5);
            setRandomOrder(newShuffled);
          }
        }, pauseBetween);
      }
    }

    return () => clearTimeout(timeoutId);
  }, [
    displayText,
    currentIndex,
    isTyping,
    suggestions,
    randomOrder,
    typingSpeed,
    deletingSpeed,
    pauseAtFull,
    pauseBetween,
  ]);

  const cursor = showCursor && displayText.length > 0 ? '|' : '';

  return {
    currentPlaceholder: displayText + cursor,
  };
};

export default useAnimatedPlaceholder;
